import { Box, Card, CssBaseline, useMediaQuery } from "@mui/material";
import React from "react";
import styled, { createGlobalStyle } from "styled-components";
import AppTheme from "../../../theme/AppTheme";
import Header from "../Static/Header";
import ContentLayout from "../Static/ContentLayout";
import Footer from "../Static/Footer";
import { useSellerOrgConfig } from "../../../Hooks/useSellerOrgConfig";
import { isMsmeBuild } from "../../../constants/partnerBrand";
import MsmeAuthParallaxBackground from "../Static/MsmeAuthParallaxBackground";

interface AuthLayoutProps {
  children: React.ReactNode;
  disableCustomTheme?: boolean;
  props?: any;
}

const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
  }
  html,
  body,
  #root {
    margin: 0;
    padding: 0;
    min-height: 100dvh;
    background-color: #f5f5f5;
  }
`;

const Container = styled.div<{ $msmeAuth?: boolean }>`
  background-color: ${({ $msmeAuth }) => ($msmeAuth ? "#f9fafb" : "#f5f5f5")};
  min-height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const BoxStaticContentLayout = styled(Box)`
  flex: 50%;
  place-items: center;
`;
const BoxFormContentLayout = styled(Box)`
  flex: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
  margin: 2rem 0;

  @media (max-width: 767px) {
    flex: 0 0 auto;
    justify-content: flex-start;
    margin: 1rem 0 0;
  }
`;

const ContentSection = styled(Box)`
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  min-height: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }
`;

const AuthLayout: React.FC<AuthLayoutProps> = ({ children, disableCustomTheme, props }) => {
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:850px)");
  const isMobile = useMediaQuery("(max-width:767px)");
  const { branding } = useSellerOrgConfig();
  const hasCustomAuthFooter = Boolean(branding.authFooterText);
  const showFooter = hasCustomAuthFooter || (!isMobile && !isTablet);
  const compactMobileFooter = isMobile && hasCustomAuthFooter;
  const msmeDesktopMarketing = isMsmeBuild() && !isMobile && !isTablet;
  const msmeAuthShell = isMsmeBuild();

  const loginCard = (
    <Card
      sx={{
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(25px)",
        WebkitBackdropFilter: "blur(25px)",
        boxShadow: "0px 20px 20px 0px rgba(0, 0, 0, 0.1)",
        pt: msmeAuthShell
          ? isTablet
            ? "24px"
            : isMobile
              ? "20px"
              : "32px"
          : 0,
        pr: isTablet ? "20px" : isMobile ? "20px" : "50px",
        pb: "20px",
        pl: isTablet ? "20px" : isMobile ? "20px" : "50px",
        borderRadius: "20px",
        "@media (min-width: 1024px)": {
          width: "70%",
        },
        "@media (min-width: 768px) and (max-width: 1023px)": {
          width: isTablet ? "calc(100vw - 10vw)" : "70%",
        },
        "@media (max-width: 767px)": {
          width: "calc(100vw - 5vw)",
        },
      }}
    >
      {children}
    </Card>
  );

  const authMainContent = msmeDesktopMarketing ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <BoxStaticContentLayout
        sx={{
          flex: "50%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          placeItems: "unset",
        }}
      >
        <ContentLayout />
      </BoxStaticContentLayout>
      <BoxFormContentLayout
        sx={{
          flex: "50%",
          margin: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loginCard}
      </BoxFormContentLayout>
    </Box>
  ) : (
    <>
      {!isMobile && !isTablet && (
        <BoxStaticContentLayout>
          <ContentLayout />
        </BoxStaticContentLayout>
      )}
      <BoxFormContentLayout>{loginCard}</BoxFormContentLayout>
    </>
  );

  return (
    <AppTheme disableCustomTheme={disableCustomTheme} {...props}>
      <CssBaseline enableColorScheme />
      <GlobalStyle />
      <Container $msmeAuth={msmeAuthShell}>
        <Box
          sx={{
            position: "sticky",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            flexShrink: 0,
          }}
        >
          <Header />
        </Box>
        <ContentSection
          sx={{
            ...(compactMobileFooter ? { pb: "72px" } : {}),
            ...(msmeDesktopMarketing
              ? {
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "stretch",
                }
              : {}),
          }}
        >
          {msmeAuthShell ? (
            <MsmeAuthParallaxBackground>{authMainContent}</MsmeAuthParallaxBackground>
          ) : (
            authMainContent
          )}
        </ContentSection>
        {showFooter ? (
          <Box
            sx={
              compactMobileFooter
                ? {
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 900,
                    width: "100%",
                  }
                : {
                    flexShrink: 0,
                    width: "100%",
                    mt: "auto",
                  }
            }
          >
            <Footer compactMobile={compactMobileFooter} />
          </Box>
        ) : null}
      </Container>
    </AppTheme>
  );
};

export default AuthLayout;
